import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import TimerRoundedIcon from "@mui/icons-material/TimerRounded";
import { compactActionSx, surfaceSx } from "./ownerCenterUi";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function clockMinutes(value) {
  const match = String(value || "").match(/^(\d{2}):(\d{2})$/);
  return match ? (Number(match[1]) * 60) + Number(match[2]) : 0;
}

function localMinutes(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? (date.getHours() * 60) + date.getMinutes() : null;
}

function ymd(value = new Date()) {
  const pad = (number) => String(number).padStart(2, "0");
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

function localDateTimeInput(dateKey, minutes) {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${dateKey}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function hourLabel(minutes) {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:00`;
}

function formatClock(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function durationLabel(minutes) {
  const total = Math.max(0, Math.round(Number(minutes || 0)));
  if (total < 60) return `${total} min`;
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function overlaps(start, end, slotStart, slotEnd) {
  return start < slotEnd && end > slotStart;
}

function plannedBlocks(calendar) {
  const day = calendar?.day || {};
  const blocks = [];
  (day.routines || []).forEach((routine) => {
    const start = clockMinutes(routine.startTime);
    const end = clockMinutes(routine.endTime);
    if (end <= start) return;
    blocks.push({
      id: `routine:${idOf(routine)}`,
      type: "routine",
      title: routine.name,
      start,
      end,
      completed: Boolean(routine.completed),
      completedAt: routine.completedAt,
      source: routine,
    });
  });
  (day.commitments || []).forEach((item) => {
    const start = localMinutes(item.scheduledStartAt);
    if (start === null) return;
    const duration = Math.max(15, Number(item.estimatedMinutes || 45));
    blocks.push({
      id: `commitment:${idOf(item)}`,
      type: "commitment",
      title: item.title,
      start,
      end: start + duration,
      completed: item.status === "done",
      actualMinutes: Number(item.actualMinutes || 0),
      source: item,
    });
  });
  return blocks;
}

function actualBlocks(calendar) {
  return (calendar?.day?.timeEntries || []).map((entry) => {
    const start = localMinutes(entry.occurredAt);
    if (start === null) return null;
    const minutes = Math.max(1, Number(entry.minutes || 0));
    return {
      id: `actual:${idOf(entry)}`,
      type: "actual",
      title: entry.commitment?.title || entry.note || "Trabajo registrado",
      start,
      end: start + minutes,
      minutes,
      note: entry.note,
      source: entry,
    };
  }).filter(Boolean);
}

function SlotItem({ block, onLogTime }) {
  if (block.type === "actual") {
    return (
      <Box sx={{ p: .8, borderRadius: 1.6, bgcolor: "action.hover" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 950, fontSize: 12.5, lineHeight: 1.25 }}>{block.title}</Typography>
          <Chip size="small" label={`Hecho · ${block.minutes} min`} sx={{ height: 21, fontWeight: 900, fontSize: 10.5 }} />
        </Stack>
        {block.note ? <Typography sx={{ mt: .25, color: "text.secondary", fontWeight: 700, fontSize: 11.2 }}>{block.note}</Typography> : null}
      </Box>
    );
  }

  return (
    <Box sx={{ p: .8, borderRadius: 1.6, border: "1px solid", borderColor: "divider" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.5} alignItems={{ sm: "center" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontWeight: 950, fontSize: 12.5, lineHeight: 1.25 }}>{block.title}</Typography>
            <Chip size="small" variant="outlined" label={block.type === "routine" ? "Rutina" : "Plan"} sx={{ height: 19, fontWeight: 850, fontSize: 9.8 }} />
            {block.completed ? <Chip size="small" label="Hecho" sx={{ height: 19, fontWeight: 850, fontSize: 9.8 }} /> : null}
          </Stack>
          <Typography sx={{ mt: .15, color: "text.secondary", fontWeight: 720, fontSize: 10.9 }}>
            {`${String(Math.floor(block.start / 60)).padStart(2, "0")}:${String(block.start % 60).padStart(2, "0")}`}–{`${String(Math.floor(block.end / 60)).padStart(2, "0")}:${String(block.end % 60).padStart(2, "0")}`}
            {block.type === "routine" && block.completedAt ? ` · marcada ${formatClock(block.completedAt)}` : ""}
            {block.type === "commitment" && block.actualMinutes ? ` · ${block.actualMinutes} min reales hoy` : ""}
          </Typography>
        </Box>
        {block.type === "commitment" && !block.completed ? (
          <Button size="small" startIcon={<TimerRoundedIcon />} onClick={() => onLogTime?.(block.source)} sx={{ ...compactActionSx, minWidth: 0, flexShrink: 0 }}>
            Registrar
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
}

export function getDayOccupiedIntervals(calendar) {
  return plannedBlocks(calendar).map((block) => ({ start: block.start, end: block.end }));
}

export default function OwnerHourBoard({ calendar, onScheduleAt, onLogTime, onLogAt }) {
  const selectedDate = calendar?.selectedDate || ymd();
  const settings = calendar?.settings || {};
  const today = selectedDate === ymd();
  const now = new Date();
  const nowMinutes = (now.getHours() * 60) + now.getMinutes();
  const configuredStart = clockMinutes(settings.workdayStart || "09:00");
  const configuredEnd = clockMinutes(settings.workdayEnd || "18:00");
  const firstDataMinute = Math.min(
    configuredStart,
    ...plannedBlocks(calendar).map((block) => block.start),
    ...actualBlocks(calendar).map((block) => block.start),
  );
  const lastDataMinute = Math.max(
    configuredEnd,
    ...plannedBlocks(calendar).map((block) => block.end),
    ...actualBlocks(calendar).map((block) => block.end),
    today ? nowMinutes + 120 : configuredEnd,
  );
  const startHour = Math.max(0, Math.floor(firstDataMinute / 60) * 60);
  const endHour = Math.min(24 * 60, Math.ceil(lastDataMinute / 60) * 60);
  const slots = [];
  for (let minute = startHour; minute < endHour; minute += 60) slots.push(minute);

  const planned = plannedBlocks(calendar);
  const actual = actualBlocks(calendar);
  const actualMinutes = actual.reduce((sum, block) => sum + Number(block.minutes || 0), 0);

  return (
    <Box sx={{ ...surfaceSx, mt: 1, p: { xs: 1.25, sm: 1.7 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.8} alignItems={{ sm: "center" }}>
        <Box>
          <Stack direction="row" spacing={.65} alignItems="center">
            <AccessTimeRoundedIcon fontSize="small" />
            <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>Tu día por horas</Typography>
          </Stack>
          <Typography sx={{ mt: .2, color: "text.secondary", fontWeight: 720, fontSize: 12.3 }}>
            Lo planificado queda visible; cuando registrás trabajo, la hora se va llenando con lo que realmente hiciste.
          </Typography>
        </Box>
        <Stack direction="row" spacing={.55} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button size="small" startIcon={<TimerRoundedIcon />} onClick={() => onLogAt?.(localDateTimeInput(selectedDate, today ? nowMinutes : configuredStart))} sx={{ ...compactActionSx, minWidth: 0 }}>Registrar lo que hice</Button>
          <Chip size="small" label={`${durationLabel(actualMinutes)} reales`} sx={{ fontWeight: 900 }} />
          <Chip size="small" label={today ? `Ahora ${formatClock(now)}` : selectedDate} sx={{ fontWeight: 900 }} />
        </Stack>
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Stack spacing={0}>
        {slots.map((slotStart) => {
          const slotEnd = slotStart + 60;
          const slotPlanned = planned.filter((block) => overlaps(block.start, block.end, slotStart, slotEnd));
          const slotActual = actual.filter((block) => overlaps(block.start, block.end, slotStart, slotEnd));
          const isCurrent = today && nowMinutes >= slotStart && nowMinutes < slotEnd;
          const isPast = today && slotEnd <= nowMinutes;
          const canSchedule = !isPast && !slotPlanned.length;
          return (
            <Box key={slotStart} sx={{ display: "grid", gridTemplateColumns: { xs: "48px minmax(0,1fr)", sm: "62px minmax(0,1fr)" }, gap: { xs: .7, sm: 1 }, py: .65, borderBottom: "1px solid", borderColor: "divider", opacity: isPast && !slotActual.length ? .72 : 1 }}>
              <Box sx={{ pt: .25 }}>
                <Typography sx={{ fontWeight: 950, fontSize: 11.5, color: isCurrent ? "primary.main" : "text.secondary" }}>{hourLabel(slotStart)}</Typography>
                {isCurrent ? <Typography sx={{ mt: .15, color: "primary.main", fontWeight: 950, fontSize: 9.5 }}>AHORA</Typography> : null}
              </Box>
              <Stack spacing={.45} sx={{ minWidth: 0 }}>
                {slotActual.map((block) => <SlotItem key={block.id} block={block} onLogTime={onLogTime} />)}
                {slotPlanned.map((block) => <SlotItem key={block.id} block={block} onLogTime={onLogTime} />)}
                {!slotActual.length && !slotPlanned.length ? (
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ minHeight: 32 }}>
                    <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: 11.5 }}>{isPast ? "Sin registro" : "Hueco libre"}</Typography>
                    {canSchedule && onScheduleAt ? (
                      <Button
                        size="small"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => onScheduleAt(localDateTimeInput(selectedDate, slotStart))}
                        sx={{ ...compactActionSx, minWidth: 0, px: .75 }}
                      >
                        Ocupar
                      </Button>
                    ) : isPast ? (
                      <Button
                        size="small"
                        startIcon={<TimerRoundedIcon />}
                        onClick={() => onLogAt?.(localDateTimeInput(selectedDate, slotStart))}
                        sx={{ ...compactActionSx, minWidth: 0, px: .75 }}
                      >
                        Registrar
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
